import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import {
  Calendar, Truck, ArrowLeft, Car, User, MapPin, CreditCard, Clock
} from 'lucide-react';
import axios from 'axios';
import PublicHeader from '../public/PublicHeader';
import PublicFooter from '../public/PublicFooter';
import { BACKEND_URL_EXPORT as BACKEND_URL } from '../../config';

const MyReservations = () => {
  const { getAuthHeaders } = useAuth();
  const navigate = useNavigate();
  const [reservations, setReservations] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchReservations();
  }, []);

  const fetchReservations = async () => {
    try {
      const response = await axios.get(`${BACKEND_URL}/api/public/customer/my-reservations`, {
        headers: getAuthHeaders()
      });
      setReservations(Array.isArray(response.data) ? response.data : []);
    } catch (error) {
      console.error('Error fetching reservations:', error);
    } finally {
      setLoading(false);
    }
  };

  const getStatusColor = (status) => {
    const statusLower = status?.toLowerCase() || '';
    if (statusLower === 'pending') return 'bg-yellow-100 text-yellow-700';
    if (statusLower === 'approved') return 'bg-blue-100 text-blue-700';
    if (statusLower === 'confirmed') return 'bg-blue-100 text-blue-700';
    if (statusLower === 'converted') return 'bg-green-100 text-green-700';
    if (statusLower === 'cancelled') return 'bg-red-100 text-red-700';
    if (statusLower === 'expired') return 'bg-orange-100 text-orange-700';
    return 'bg-gray-100 text-gray-700';
  };

  const formatDate = (dateString) => {
    if (!dateString) return null;
    try {
      return new Date(dateString).toLocaleDateString('en-US', {
        year: 'numeric',
        month: 'short',
        day: 'numeric'
      });
    } catch {
      return null;
    }
  };

  const formatPrice = (amount) => {
    if (amount === null || amount === undefined || amount === '') return 'N/A';
    const num = Number(amount);
    if (Number.isNaN(num)) return String(amount);
    return `$${num.toLocaleString()}`;
  };

  const getVehicle = (reservation) => reservation?.vehicle_info || null;

  const getTitle = (reservation) => {
    const v = getVehicle(reservation);
    const d = reservation?.vehicle_details || {};
    const year = v?.year || d?.year || '';
    const brand = v?.brand || '';
    const model = v?.model || d?.model || '';
    const built = `${year} ${brand} ${model}`.trim();
    return built || 'Vehicle';
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50">
        <PublicHeader currency="USD" onCurrencyChange={() => {}} />
        <div className="flex items-center justify-center h-96">
          <div className="text-center">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-red-600 mx-auto"></div>
            <p className="text-gray-600 mt-4">Loading reservations...</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <PublicHeader currency="USD" onCurrencyChange={() => {}} />

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Header */}
        <div className="mb-8">
          <Button
            onClick={() => navigate('/customer/dashboard')}
            variant="ghost"
            className="mb-4"
          >
            <ArrowLeft className="h-4 w-4 mr-2" />
            Back to Dashboard
          </Button>
          <h1 className="text-3xl font-bold text-gray-900">My Reservations</h1>
          <p className="text-gray-600 mt-1">Vehicles you have reserved — our team will be in touch to confirm</p>
        </div>

        {/* Reservations List */}
        {reservations.length === 0 ? (
          <Card>
            <CardContent className="p-12 text-center">
              <Calendar className="h-16 w-16 text-gray-400 mx-auto mb-4" />
              <h3 className="text-xl font-semibold text-gray-900 mb-2">No reservations yet</h3>
              <p className="text-gray-600 mb-6">Vehicles you reserve will appear here</p>
              <Button
                onClick={() => navigate('/vehicles')}
                className="bg-gradient-to-r from-red-600 to-red-700 hover:from-red-700 hover:to-red-800"
              >
                Browse Vehicles
              </Button>
            </CardContent>
          </Card>
        ) : (
          <div className="space-y-4">
            {reservations.map((reservation) => {
              const vehicle = getVehicle(reservation);
              const details = reservation?.vehicle_details || {};
              const image = vehicle?.images?.[0] || null;
              const stockNo = vehicle?.stock_no || details?.stock_no;
              const price = vehicle?.public_price_usd ?? details?.price ?? details?.price_usd;
              const vehicleStatus = vehicle?.status;

              return (
                <Card key={reservation.id} className="hover:shadow-lg transition-shadow">
                  <CardContent className="p-6">
                    <div className="grid grid-cols-1 lg:grid-cols-4 gap-6">
                      {/* Vehicle Image */}
                      <div className="lg:col-span-1">
                        {image ? (
                          <img
                            src={image}
                            alt={getTitle(reservation)}
                            className="w-full h-32 object-cover rounded-lg"
                          />
                        ) : (
                          <div className="w-full h-32 bg-gray-200 rounded-lg flex items-center justify-center">
                            <Car className="h-8 w-8 text-gray-400" />
                          </div>
                        )}
                      </div>

                      {/* Reservation Details */}
                      <div className="lg:col-span-3">
                        <div className="flex items-start justify-between mb-4">
                          <div className="flex-1">
                            <div className="flex items-center space-x-2 mb-2">
                              <h3 className="text-lg font-semibold text-gray-900">
                                {getTitle(reservation)}
                              </h3>
                              <Badge className={getStatusColor(reservation.status)}>
                                {reservation.status || 'Pending'}
                              </Badge>
                            </div>
                            {stockNo && (
                              <p className="text-sm text-gray-600">Stock No: {stockNo}</p>
                            )}
                            <p className="text-sm text-gray-600">
                              Reservation ID: #{reservation.id ? reservation.id.slice(-8).toUpperCase() : 'N/A'}
                            </p>
                          </div>
                          <div className="text-right">
                            <p className="text-2xl font-bold text-red-600">
                              {formatPrice(price)}
                            </p>
                            {vehicleStatus && (
                              <Badge variant="outline" className="mt-1">
                                Vehicle: {vehicleStatus}
                              </Badge>
                            )}
                          </div>
                        </div>

                        <div className="grid grid-cols-2 md:grid-cols-3 gap-4 text-sm">
                          <div>
                            <div className="flex items-center text-gray-600 mb-1">
                              <Calendar className="h-4 w-4 mr-1" />
                              <span>Reserved On</span>
                            </div>
                            <p className="font-semibold">{formatDate(reservation.created_at) || 'N/A'}</p>
                          </div>

                          <div>
                            <div className="flex items-center text-gray-600 mb-1">
                              <CreditCard className="h-4 w-4 mr-1" />
                              <span>Payment Preference</span>
                            </div>
                            <p className="font-semibold">{reservation.payment_preference || 'Bank Transfer'}</p>
                          </div>

                          <div>
                            <div className="flex items-center text-gray-600 mb-1">
                              <User className="h-4 w-4 mr-1" />
                              <span>Contact Name</span>
                            </div>
                            <p className="font-semibold">{reservation.customer_name || 'N/A'}</p>
                          </div>

                          <div>
                            <div className="flex items-center text-gray-600 mb-1">
                              <Truck className="h-4 w-4 mr-1" />
                              <span>Phone</span>
                            </div>
                            <p className="font-semibold">{reservation.customer_phone || 'N/A'}</p>
                          </div>

                          <div>
                            <div className="flex items-center text-gray-600 mb-1">
                              <MapPin className="h-4 w-4 mr-1" />
                              <span>Location</span>
                            </div>
                            <p className="font-semibold">
                              {[reservation.customer_city, reservation.customer_country].filter(Boolean).join(', ') || 'N/A'}
                            </p>
                          </div>

                          <div>
                            <div className="flex items-center text-gray-600 mb-1">
                              <Clock className="h-4 w-4 mr-1" />
                              <span>Status</span>
                            </div>
                            <p className="font-semibold capitalize">{reservation.status || 'Pending'}</p>
                          </div>
                        </div>

                        {reservation.additional_notes && (
                          <div className="mt-4 p-3 bg-gray-50 rounded-lg">
                            <p className="text-sm text-gray-600">
                              <strong>Notes:</strong> {reservation.additional_notes}
                            </p>
                          </div>
                        )}

                        {reservation.vehicle_id && (
                          <div className="mt-4 flex justify-end">
                            <Button
                              onClick={() => navigate(`/vehicles/${reservation.vehicle_id}`)}
                              variant="outline"
                              className="flex items-center gap-2"
                            >
                              <Car className="h-4 w-4" />
                              View Vehicle
                            </Button>
                          </div>
                        )}
                      </div>
                    </div>
                  </CardContent>
                </Card>
              );
            })}
          </div>
        )}
      </div>

      <PublicFooter />
    </div>
  );
};

export default MyReservations;
